import { CSSProperties, useContext, useEffect, useState } from "react";

import styles from "./LoyaltyBalance.module.css";
import SettingsContext from "~/contexts/SettingsContext";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import AuthContext from "~/contexts/AuthContext";
import ModalContext from "~/contexts/ModalContext";
import { useLocation } from "@remix-run/react";
import { formatLoyaltyBalance } from "~/helpers/numberHelpers";
import JoinLoyaltyModal from "../JoinLoyaltyModal/JoinLoyaltyModal";
import LoyaltyEarnings from "../LoyaltyEarnings/LoyaltyEarnings";
import ThemeContext from "~/contexts/ThemeContext";
import { InfoBarPointsSize } from "~/theme/models/themeModels";
import { isPaymentPage } from "~/helpers/routesHelpers";

type LoyaltyBalanceProps = {
  fontColor: string;
};

const LoyaltyBalance: React.FC<LoyaltyBalanceProps> = ({ fontColor }) => {
  const [visibleJoinLoyaltyModal, setVisibleJoinLoyaltyModal] = useState(false);

  const { orgSettings } = useContext(SettingsContext);
  const { setShowAssetModal } = useContext(ModalContext);
  const { authenticatedUser, useComoAuth } = useContext(AuthContext);
  const { template } = useContext(ThemeContext);

  const location = useLocation();
  const readonlyLoyalty = isPaymentPage(location.pathname);

  const hasLoyalty = !!(
    authenticatedUser?.loyaltyBalance != null &&
    authenticatedUser.loyaltyCheckbox
  );

  useEffect(() => {
    if (hasLoyalty && visibleJoinLoyaltyModal) {
      setVisibleJoinLoyaltyModal(false);
    }
  }, [hasLoyalty, visibleJoinLoyaltyModal]);

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (hasLoyalty || !useComoAuth) {
      if (readonlyLoyalty) return;

      setShowAssetModal(true);
    } else {
      setVisibleJoinLoyaltyModal(true);
    }
  };

  // Texts
  const viewYourClubGoods = template.loyalty.text.viewYourClubGoods;
  const mobilePointsColor = template.infoBar.mobilePoints.fontColour;
  const mobileViewButtonColor = template.infoBar.mobilePoints.viewButtonColour;

  // Points
  const pointsStyles: CSSProperties = {
    fontSize: InfoBarPointsSize[template.infoBar.points.fontSize],
    color: template.infoBar.points.fontColour,
  };

  const showLoyalty = hasLoyalty;
  const showViewAssets = hasLoyalty || !useComoAuth;
  const showPotentialEarnings = useComoAuth && !hasLoyalty;

  return (
    <div
      className={styles.container}
      style={{
        color: fontColor,
      }}
    >
      <JoinLoyaltyModal
        visible={visibleJoinLoyaltyModal}
        onClose={() => setVisibleJoinLoyaltyModal(false)}
      />
      {/** Desktop View */}
      <div className={styles.desktop} onClick={onClick}>
        {hasLoyalty && (
          <>
            <span>You have</span>
            <b style={pointsStyles}>
              {formatLoyaltyBalance(
                orgSettings,
                authenticatedUser?.loyaltyBalance,
              )}
            </b>
            <span>
              {orgSettings?.loyaltySettings?.balanceUnitName ?? "balance"}
            </span>
            {orgSettings?.loyaltySettings?.loyaltyIcon && (
              <img
                src={uploadcareConfig.getUrl(
                  orgSettings?.loyaltySettings?.loyaltyIcon.fileId,
                  orgSettings?.loyaltySettings?.loyaltyIcon.modifiers,
                )}
                alt={orgSettings?.loyaltySettings?.loyaltyIcon.fileName}
                className={styles.loyaltyIcon}
              />
            )}
          </>
        )}
        {showViewAssets && (
          <span className={styles.viewButton} style={{ marginLeft: "16px" }}>
            {viewYourClubGoods}
          </span>
        )}
        {showPotentialEarnings && <LoyaltyEarnings />}
      </div>
      {/** Mobile View */}
      <div className={styles.mobile}>
        {showLoyalty && (
          <>
            {orgSettings?.loyaltySettings?.loyaltyIcon && (
              <img
                src={uploadcareConfig.getUrl(
                  orgSettings?.loyaltySettings?.loyaltyIcon.fileId,
                  orgSettings?.loyaltySettings?.loyaltyIcon.modifiers,
                )}
                alt={orgSettings?.loyaltySettings?.loyaltyIcon.fileName}
                className={styles.loyaltyIcon}
              />
            )}
            <span>
              You have{" "}
              <b style={{ color: mobilePointsColor }}>
                {formatLoyaltyBalance(
                  orgSettings,
                  authenticatedUser?.loyaltyBalance,
                )}
              </b>{" "}
              {orgSettings?.loyaltySettings?.balanceUnitName ?? "balance"}
            </span>
          </>
        )}
        {showViewAssets && (
          <span
            className={styles.viewButton}
            style={{ color: mobileViewButtonColor }}
            onClick={onClick}
          >
            {viewYourClubGoods}
          </span>
        )}
        {showPotentialEarnings && (
          <span className={styles.mobileNoLoyalty} onClick={onClick}>
            <LoyaltyEarnings />
          </span>
        )}
      </div>
    </div>
  );
};

export default LoyaltyBalance;
